import React from 'react';
import { Bot, Sparkles, ArrowUpRight } from 'lucide-react';
import { fmtMoney, fmtMoneyCompact, rowViewOf, styleColorOf, exposureOf, timeLabel } from './useAgentTickets';
import { SkeletonRows, EmptyGoodNews, ErrorBanner } from './carbonStates';

// ============================================================================
// CHA-272 — "Today" home view, live data (supersedes the CHA-240 static v1)
// ============================================================================
// Purely presentational: consumes the `today` view model from useAgentTickets
// (wired in InMoceanDashboard) so the KPI strip, worklist, sidebar badge and
// cockpit all derive from the SAME numbers (CHA-239 consistency requirement).
// Yulie = purple identity, Ella = teal identity. Uses the design's ~1360px /
// 28-32px envelope (NOT the p-6 max-w-7xl envelope of non-Today routes).
const WORKLIST_SIZE = 5;

export default function TodayHome({ user, userProfile, navigateTo, today }) {
  const first =
    (userProfile?.displayName || userProfile?.name || user?.displayName || user?.email?.split('@')[0] || 'there')
      .trim()
      .split(' ')[0];
  const goAgents = () => navigateTo && navigateTo('agents');

  const {
    ranked = [],
    openCount = 0,
    urgentCount = 0,
    openExposure = 0,
    clearedCount = 0,
    clearedExposure = 0,
    avgClearDays = null,
    agents = [],
    feed = [],
    loading = false,
    error = null,
  } = today || {};

  const worklist = ranked.slice(0, WORKLIST_SIZE);
  const moreCount = Math.max(0, openCount - worklist.length);
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  const kpis = [
    { label: 'At-risk exposure', value: fmtMoneyCompact(openExposure), note: 'open queue', color: '#161616' },
    {
      label: 'Open exceptions',
      value: String(openCount),
      note: urgentCount > 0 ? `${urgentCount} urgent` : 'none urgent',
      color: '#161616',
    },
    {
      label: 'Cleared this week',
      value: String(clearedCount),
      note: clearedCount > 0 ? `${fmtMoneyCompact(clearedExposure)} resolved` : 'past 7 days',
      color: '#24A148',
    },
    {
      label: 'Time-to-clear',
      value: avgClearDays == null ? '—' : `${avgClearDays.toFixed(1)}d`,
      note: 'avg, cleared this week',
      color: '#161616',
    },
  ];

  return (
    <div className="font-plex text-carbon-ink px-8 pt-7 pb-12 max-w-[1360px]">
      {/* Greeting */}
      <div className="mb-6">
        <div className="text-[22px] font-semibold tracking-[-0.01em]">
          {greeting}, {first}.
        </div>
        <div className="text-sm text-carbon-gray-70 mt-1">
          {error && openCount === 0 ? (
            // Never claim "queue clear" when the subscription failed — an
            // empty array on error is unknown, not good news.
            <>Live agent data is unavailable — the queue state is unknown.</>
          ) : openCount === 0 ? (
            <>
              Queue clear — nothing needs a call.{' '}
              <span className="font-medium text-carbon-purple-70">Yulie</span> and{' '}
              <span className="font-medium text-carbon-teal-60">Ella</span> are watching.
            </>
          ) : (
            <>
              <span className="font-semibold text-carbon-ink">
                {urgentCount > 0 ? `${urgentCount} item${urgentCount === 1 ? '' : 's'}` : `${openCount} item${openCount === 1 ? '' : 's'}`}
              </span>{' '}
              need{(urgentCount || openCount) === 1 ? 's' : ''} your call.{' '}
              <span className="font-medium text-carbon-purple-70">Yulie</span> and{' '}
              <span className="font-medium text-carbon-teal-60">Ella</span> are watching the rest.
            </>
          )}
        </div>
      </div>

      {error ? <ErrorBanner what="Live agent data is unavailable." staleNote={error} /> : null}

      {/* KPI strip — Carbon tile grid, 1px gaps on the border */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-px bg-carbon-border border border-carbon-border mb-6">
        {kpis.map((k) => (
          <div key={k.label} className="bg-carbon-white px-[18px] py-4">
            <div className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mb-2">
              {k.label}
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-plex-mono text-2xl font-semibold" style={{ color: k.color }}>
                {loading ? '…' : k.value}
              </span>
              <span className="text-[11.5px] text-carbon-gray-50">{k.note}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-6 items-start">
        {/* Needs your call */}
        <div className="basis-[480px] grow-[999] shrink min-w-0 bg-carbon-white border border-carbon-border">
          <div className="flex items-center justify-between px-[18px] py-3.5 border-b border-carbon-border">
            <div className="text-sm font-semibold">Needs your call</div>
            <button
              type="button"
              onClick={goAgents}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-carbon-blue-60 hover:text-carbon-blue-70"
            >
              Open agents cockpit <ArrowUpRight size={13} />
            </button>
          </div>

          {loading ? (
            <SkeletonRows rows={4} />
          ) : worklist.length === 0 && error ? (
            <div className="px-[18px] py-8 text-center text-xs text-carbon-gray-50">
              Queue unavailable — see the notice above.
            </div>
          ) : worklist.length === 0 ? (
            <EmptyGoodNews detail="No open exceptions across Yulie and Ella." />
          ) : (
            worklist.map((t, i) => {
              const row = rowViewOf(t);
              const showActions = i === 0;
              return (
                <div
                  key={t.id}
                  className="relative flex items-center gap-3.5 px-[18px] py-3 border-t border-carbon-gray-10"
                  style={{ background: i === 0 ? '#FAFAFA' : '#FFFFFF' }}
                >
                  {/* 3px severity rail */}
                  <span className="absolute left-0 top-2 bottom-2 w-[3px]" style={{ background: row.sev }} />
                  <div className="w-[58px] flex-shrink-0">
                    <div className="font-plex-mono text-[15px] font-semibold leading-none" style={{ color: row.sev }}>
                      {row.metric}
                    </div>
                    <div className="font-plex-mono text-[9px] tracking-[0.08em] uppercase text-carbon-gray-50 mt-0.5">
                      {row.caption}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold text-carbon-ink">
                      PO {t.po_no || t.id}{' '}
                      <span className="font-normal text-carbon-gray-50 text-xs">{styleColorOf(t)}</span>
                      {t.state === 'snoozed' ? (
                        <span className="ml-1.5 font-plex-mono text-[9px] tracking-[0.08em] uppercase text-carbon-gray-40 border border-carbon-gray-20 px-1 py-px align-middle">
                          snoozed
                        </span>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-carbon-gray-70 mt-0.5 min-w-0">
                      <Sparkles size={11} style={{ color: row.agent.color }} />
                      <span className="font-semibold flex-shrink-0" style={{ color: row.agent.color }}>
                        {row.agent.name}
                      </span>
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap">· {row.suggested}</span>
                    </div>
                  </div>
                  {showActions ? (
                    <span className="flex gap-1.5 flex-shrink-0">
                      <button
                        type="button"
                        onClick={goAgents}
                        title="Opens the agents cockpit"
                        className="text-xs font-medium bg-carbon-ink text-carbon-white px-3 py-1.5 hover:bg-carbon-gray-80"
                      >
                        Snooze
                      </button>
                      <button
                        type="button"
                        onClick={goAgents}
                        title="Opens the agents cockpit"
                        className="text-xs font-medium bg-carbon-white border border-carbon-gray-30 text-carbon-ink px-3 py-1.5 hover:bg-carbon-gray-10"
                      >
                        Mark OK
                      </button>
                    </span>
                  ) : (
                    <div className="font-plex-mono text-[13px] font-semibold text-carbon-ink flex-shrink-0">
                      {fmtMoney(exposureOf(t))}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {!loading && moreCount > 0 ? (
            <div className="px-[18px] py-2.5 border-t border-carbon-border font-plex-mono text-[10.5px] text-carbon-gray-50">
              + {moreCount} MORE IN THE AGENTS COCKPIT
            </div>
          ) : null}
        </div>

        {/* Right rail */}
        <div className="basis-[300px] grow shrink flex flex-col gap-6">
          {/* Agents status card */}
          <div className="bg-carbon-white border border-carbon-border px-[18px] py-4">
            <div className="font-plex-mono text-[10px] tracking-[0.14em] uppercase text-carbon-gray-50 mb-3">
              Agents
            </div>
            <div className="flex flex-col gap-3">
              {agents.map((a) => (
                <AgentRow
                  key={a.rule}
                  name={a.name}
                  sub={a.sub}
                  stat={
                    // No 'idle' claim when run telemetry doesn't exist —
                    // tickets carry no last_checked (see useAgentTickets).
                    (a.lastRun ? `ran ${timeLabel(a.lastRun)} · ` : '') +
                    `${a.openCount} open` +
                    (a.newToday > 0 ? ` · ${a.newToday} new` : '')
                  }
                  color={a.color}
                  tint={a.tint}
                />
              ))}
            </div>
          </div>

          {/* Activity feed */}
          <div className="bg-carbon-white border border-carbon-border px-[18px] py-4">
            <div className="font-plex-mono text-[10px] tracking-[0.14em] uppercase text-carbon-gray-50 mb-2.5">
              Activity
            </div>
            {feed.length === 0 ? (
              <div className="text-xs text-carbon-gray-50 py-1.5">No recent activity.</div>
            ) : (
              feed.map((f, i) => (
                <div key={i} className="flex gap-2.5 py-[5px] border-t border-carbon-gray-10">
                  <span className="font-plex-mono text-[10px] text-carbon-gray-30 w-12 flex-shrink-0 pt-0.5">
                    {f.time}
                  </span>
                  <div className="text-xs leading-normal text-carbon-gray-70">
                    <span className="font-semibold" style={{ color: f.whoColor }}>
                      {f.who}
                    </span>{' '}
                    {f.text}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentRow({ name, sub, stat, color, tint }) {
  return (
    <div className="flex gap-2.5 items-center">
      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ background: tint, color }}>
        <Bot size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold">
          {name} <span className="font-normal text-carbon-gray-50">· {sub}</span>
        </div>
        <div className="font-plex-mono text-[10px] text-carbon-gray-50">{stat}</div>
      </div>
      <span className="w-[7px] h-[7px] rounded-full bg-carbon-green-50 animate-pulse flex-shrink-0" />
    </div>
  );
}
